import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { 
  Gift, 
  Users, 
  TrendingUp, 
  Award,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const ReferralManager = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_codes: 0,
    active_codes: 0,
    total_referrals: 0,
    successful_referrals: 0,
    total_membership_days_given: 0,
    top_referrers: []
  });
  const [logs, setLogs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [codesPage, setCodesPage] = useState(1);
  const [logsPagination, setLogsPagination] = useState({ total: 0, pages: 1 });
  const [codesPagination, setCodesPagination] = useState({ total: 0, pages: 1 });
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, [logsPage, codesPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch stats
      const statsRes = await fetch(`${API_BASE}/api/admin/referral-stats`, { headers });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      
      // Fetch logs
      const logsRes = await fetch(`${API_BASE}/api/admin/referral-logs?page=${logsPage}&limit=10`, { headers });
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setLogs(logsData.logs);
        setLogsPagination({ total: logsData.total, pages: logsData.pages });
      }
      
      // Fetch codes
      const codesRes = await fetch(`${API_BASE}/api/admin/referral-codes?page=${codesPage}&limit=10`, { headers });
      if (codesRes.ok) {
        const codesData = await codesRes.json();
        setCodes(codesData.codes);
        setCodesPagination({ total: codesData.total, pages: codesData.pages });
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load referral data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleCodeStatus = async (code) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/referral-codes/${code}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Success',
          description: data.message
        });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle code status',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Gift className="w-8 h-8 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Referral Management</h1>
          <p className="text-gray-500 dark:text-gray-400">Track and manage trainer referral codes</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Total Codes</p>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{stats.total_codes}</p>
              </div>
              <Gift className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 dark:text-green-400 font-medium">Active Codes</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.active_codes}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Total Referrals</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.total_referrals}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">Successful</p>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{stats.successful_referrals}</p>
              </div>
              <Award className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/20 dark:to-pink-800/20 border-pink-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-pink-600 dark:text-pink-400 font-medium">Days Given</p>
                <p className="text-2xl font-bold text-pink-700 dark:text-pink-300">{stats.total_membership_days_given}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-pink-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Referrers */}
      {stats.top_referrers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {stats.top_referrers.map((referrer, index) => (
                <div key={index} className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
                    index === 0 ? 'bg-amber-100 text-amber-600' :
                    index === 1 ? 'bg-gray-200 text-gray-600' :
                    index === 2 ? 'bg-orange-100 text-orange-600' :
                    'bg-blue-100 text-blue-600'
                  }`}>
                    <span className="font-bold">#{index + 1}</span>
                  </div>
                  <p className="font-semibold text-sm truncate">{referrer.trainer_name}</p>
                  <p className="text-xs text-gray-500">{referrer.referral_count} referrals</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'logs' 
              ? 'text-red-600 border-b-2 border-red-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Referral Logs
        </button>
        <button
          onClick={() => setActiveTab('codes')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'codes' 
              ? 'text-red-600 border-b-2 border-red-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Referral Codes
        </button>
      </div>

      {/* Referral Logs */}
      {activeTab === 'logs' && (
        <Card>
          <CardHeader>
            <CardTitle>Referral Activity Log</CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No referral activity yet</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Code</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Referrer</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">New User</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="py-3 px-4">
                            <span className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                              {log.referral_code}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-medium text-sm">{log.referrer_name}</p>
                            <p className="text-xs text-gray-500">{log.referrer_email}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-medium text-sm">{log.referred_user_name}</p>
                            <p className="text-xs text-gray-500">{log.referred_user_email}</p>
                          </td>
                          <td className="py-3 px-4">
                            {log.reward_given ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                                <CheckCircle className="w-3 h-3" />
                                Rewarded
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                                <Clock className="w-3 h-3" />
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {new Date(log.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {logsPagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-gray-500">
                      Page {logsPage} of {logsPagination.pages} ({logsPagination.total} total)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLogsPage(p => Math.max(1, p - 1))}
                        disabled={logsPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLogsPage(p => Math.min(logsPagination.pages, p + 1))}
                        disabled={logsPage === logsPagination.pages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Referral Codes */}
      {activeTab === 'codes' && (
        <Card>
          <CardHeader>
            <CardTitle>All Referral Codes</CardTitle>
          </CardHeader>
          <CardContent>
            {codes.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No referral codes created yet</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Code</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Trainer</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Uses</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Created</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map((code) => (
                        <tr key={code.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="py-3 px-4">
                            <span className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                              {code.code}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-medium text-sm">{code.trainer_name}</p>
                            <p className="text-xs text-gray-500">{code.trainer_email}</p>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-semibold">{code.total_uses}</span>
                          </td>
                          <td className="py-3 px-4">
                            {code.is_active ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                                <CheckCircle className="w-3 h-3" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                                <XCircle className="w-3 h-3" />
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {new Date(code.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleCodeStatus(code.code)}
                              className="gap-1"
                            >
                              {code.is_active ? (
                                <>
                                  <ToggleRight className="w-4 h-4" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <ToggleLeft className="w-4 h-4" />
                                  Activate
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {codesPagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-gray-500">
                      Page {codesPage} of {codesPagination.pages} ({codesPagination.total} total)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCodesPage(p => Math.max(1, p - 1))}
                        disabled={codesPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCodesPage(p => Math.min(codesPagination.pages, p + 1))}
                        disabled={codesPage === codesPagination.pages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReferralManager;
